/**
 * MobiliCalendar core model types.
 *
 * The engine normalizes the existing controller output (`CalendarEvent[]` /
 * `CalendarResource[]`, see ../Calendar/controller/adapters/types) into flat,
 * immutable, pre-indexed structures so that every hot-path operation
 * (hit-testing, keyboard navigation, overlap lookup, highlight derivation)
 * is pure in-memory math — no DOM queries, no FullCalendar.
 */
import type {
  CalendarEvent,
  TimeSlotType,
  EventType,
  VisitType,
} from "../../Calendar/controller/adapters/types";
import type { MCEventKind } from "./eventKind";
import type { TherapyColorKey } from "../../../helpers/slotTherapy";

/** Static grid configuration. All geometry derives from these numbers. */
export interface MCGridConfig {
  /** First visible minute of day (07:00) */
  dayStartMin: number;
  /** Last visible minute of day (21:00) */
  dayEndMin: number;
  /** Logical slot granularity in minutes */
  slotMin: number;
  /** Pixel height of one slot */
  slotHeightPx: number;
  /** Width of the sticky time gutter */
  gutterWidthPx: number;
  /** Height of the sticky resource header */
  headerHeightPx: number;
  /** Column width bounds; actual width flexes to fill the viewport */
  minColumnWidthPx: number;
  maxColumnWidthPx: number;
}

export const DEFAULT_GRID_CONFIG: MCGridConfig = {
  dayStartMin: 7 * 60,
  dayEndMin: 21 * 60,
  slotMin: 5,
  // 11px per 5-min slot. Compact events (≤3 slots) still clear 33px for their
  // single text row, and a standard 20-min appointment keeps 44px for the
  // two-line title/time layout — while ~48 more minutes fit on screen than at
  // the previous 13px.
  slotHeightPx: 11,
  gutterWidthPx: 58,
  headerHeightPx: 46,
  minColumnWidthPx: 150,
  maxColumnWidthPx: 340,
};

/**
 * Reserved id of the LEGACY patient-availability column. The shared
 * controller still emits it for the FullCalendar scheduler; MobiliCalendar's
 * view-model builders filter it out and render patient availability as a
 * day-spanning band instead (model/patientAvailability.ts).
 */
export const PATIENT_RESOURCE_ID = "0";

/** Normalized resource (column). */
export interface MCResource {
  id: string;
  title: string;
  /** Column index in display order */
  index: number;
  /** Appointments outside working hours (main calendar mode) */
  warningCount?: number;
  /**
   * Complement of business hours for the displayed weekday, clamped to the
   * visible day range. Rendered as dimmed segments.
   */
  nonBusinessSegments: ReadonlyArray<MCTimeRange>;
}

export interface MCTimeRange {
  startMin: number;
  endMin: number;
}

/** Foreground event kinds we render as blocks. */
export interface MCEvent {
  id: string;
  resourceId: string;
  /** Minutes from midnight, clamped to the visible day */
  startMin: number;
  endMin: number;
  /** Raw times for display labels (not clamped) */
  startLabel: string;
  endLabel: string;
  title: string;
  /** Closed render/behavior classification — see model/eventKind.ts */
  kind: MCEventKind;
  eventType: EventType;
  timeSlotType: TimeSlotType;
  isCancelled: boolean;
  attended: boolean;
  hasNotes: boolean;
  containsHeatTreatment: boolean;
  isContinuous: boolean;
  isOutsideWorkingHours: boolean;
  isDraft: boolean;
  isGroupTherapist: boolean;
  patientId?: number;
  patientLabel?: string;
  appointmentId?: string;
  therapyAbbr?: string;
  /**
   * MD-232: catalog identity for the colour lookup (TherapyMethod id, or
   * PrescribedTherapy id for an unscheduled slot). Never colour by
   * `therapyAbbr` — it is renameable display text.
   */
  therapyColorKey?: TherapyColorKey;
  treatmentIndex?: string;
  visitType?: VisitType;
  visitFeeAbbr?: string;
  sublocalityAbbr?: string;
  sublocalityColor?: string;
  sublocalityName?: string;
  /** Appointment has no Rx date and no Rx number ("im Warenkorb") */
  rxInCart?: boolean;
  /** Appointment Rx exists but has no date (missing Verordnung) */
  rxMissing?: boolean;
  /** Rx is perpetual (∞ display alongside isContinuous) */
  rxPerpetual?: boolean;
  /** Attended slot has at least one documentation entry */
  wasDocumented?: boolean;
  /** Cancellation reason (shown when isCancelled) */
  cancelReason?: string;
  /** Group therapist events: participant display names */
  participantNames?: ReadonlyArray<string>;
  /** Overlap layout: horizontal lane inside the column */
  lane: number;
  laneCount: number;
  /** Original adapter event, for bridging back to existing handlers later */
  raw: CalendarEvent;
}

/** Background fills (availability, heat treatment, patient slots). */
export interface MCBackgroundSegment {
  id: string;
  resourceId: string;
  startMin: number;
  endMin: number;
  kind: "availability" | "heat" | "generic";
  title?: string;
}

/** Focus position of the keyboard cursor. */
export interface MCCellPos {
  resourceIndex: number;
  slotMin: number;
}

/** Excel-style rectangular cell range (anchor stays, head moves). */
export interface MCCellRange {
  anchor: MCCellPos;
  head: MCCellPos;
}

/** "Verschieben" mode: picked-up events awaiting placement. */
export interface MCMoveState {
  /**
   * IMMUTABLE SNAPSHOT of the picked events, captured at pick-up time.
   * Everything about the transaction (ghost geometry, validity, commit
   * payload) derives from this capture — NEVER from live index lookups — so
   * an authoritative snapshot arriving mid-drag (socket flush, optimistic
   * patch of a previous move, refetch, SPLIT/MERGE id changes) can rebuild
   * the whole index without aborting or blanking the active transaction.
   * This is the by-design replacement for the old calendar's mid-interaction
   * events freeze + dragRecovery hit-test replay.
   */
  sources: ReadonlyArray<MCEvent>;
  /** Cell the selection was grabbed at — reference for pointer deltas */
  grabCell: MCCellPos;
  /** Column offset applied to every picked event */
  deltaCols: number;
  /** Slot offset (multiples of cfg.slotMin) applied to every picked event */
  deltaSlots: number;
}

/** Stable empty set so mini-store shallow-equal bailouts work. */
export const EMPTY_ID_SET: ReadonlySet<string> = new Set();

/**
 * Parsed `data-event` payload of an external drag source (shelf card grips,
 * per-slot grips, cancellation-drawer cards, dynamic-template chips). The
 * shape is the existing `DraggableEventData` plus the template discriminators
 * — kept structural (not imported) so the model layer stays dependency-free.
 */
export interface MCExternalPayload {
  id: string;
  /** "HH:mm:ss" total duration (heat treatments already excluded) */
  duration?: string;
  title?: string;
  patient?: { id: string | number };
  frequency?: unknown;
  address?: { id: string; type: string };
  timeSlots?: Array<{
    id?: string;
    appointmentId?: string;
    type?: string;
    therapyRx?: {
      id?: string;
      therapy?: { id?: string; abbreviation?: string; duration?: number };
    };
  }>;
  /** Per-timeslot shelf drag → slot-level scheduleUnscheduled verb */
  isSingleTimeSlot?: boolean;
  /** Pinboard slot pin (still scheduled) → atomic slot-level reschedule verb */
  isScheduledSlotMove?: boolean;
  /** Dynamic Template Provider chip → create-and-schedule verb */
  isTemplate?: boolean;
  template?: unknown;
}

/** Live external-drag state (ghost position + validity), store-held. */
export interface MCExternalDragState {
  payload: MCExternalPayload;
  resourceIndex: number;
  startMin: number;
  endMin: number;
  valid: boolean;
  /**
   * Ghost retained after the drop until the receive settles / the optimistic
   * event appears in the snapshot — the receive counterpart of pending moves.
   */
  settling: boolean;
  /** Monotonic token so a stale settle can never clear a newer drag. */
  token: number;
}

/** A resolved external drop handed to the bridge. */
export interface MCExternalDrop {
  payload: MCExternalPayload;
  resourceId: string;
  start: Date;
  end: Date;
}

/** Live interaction state — kept in a mini external store (see interaction.ts). */
export interface MCInteractionState {
  focus: MCCellPos | null;
  /** Event under the keyboard cursor (ring, transient) */
  focusedEventId: string | null;
  /** Explicitly selected event (click / Enter, persistent) */
  selectedEventId: string | null;
  /** Excel-style range selection (marquee / shift+arrows), null when collapsed */
  range: MCCellRange | null;
  /** Multi-selected event ids (range- or ctrl-click-derived) */
  multiSelectedIds: ReadonlySet<string>;
  /** Active "verschieben" (pick up / place) state */
  move: MCMoveState | null;
  /** Active external drag (shelf/template payload over the grid) */
  external: MCExternalDragState | null;
}

/** One resolved move produced when placing a picked-up selection. */
export interface MCEventMove {
  event: MCEvent;
  targetResourceId: string;
  targetStartMin: number;
  targetEndMin: number;
}

/** Stats surfaced to the perf HUD. */
export interface MCStats {
  eventCount: number;
  backgroundCount: number;
  resourceCount: number;
  indexBuildMs: number;
}
